import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FetchStatus, ModalName } from '../../../../enums';
import { useCloseModal, useModalProps } from '../../../../features/modals';
import {
  useOrganization,
  useRemoveOrganizationDevice,
  useResetRemoveOrganizationDeviceStatus,
} from '../../../../features/organization';
import { Button, Modal } from '../../../common';
import { IRemoveDeviceModalProps } from './types';

export const RemoveDeviceModal = () => {
  const { t } = useTranslation();
  const {
    status: { removeDevice: status },
  } = useOrganization();
  const removeOrganizationDevice = useRemoveOrganizationDevice();
  const resetOrganizationRemoveDeviceStatus = useResetRemoveOrganizationDeviceStatus();
  const { device } = useModalProps(ModalName.REMOVE_DEVICE) as IRemoveDeviceModalProps;
  const closeModal = useCloseModal(ModalName.REMOVE_DEVICE);

  const remove = useCallback(() => {
    removeOrganizationDevice(device);
  }, [removeOrganizationDevice, device]);

  useEffect(() => {
    if (status === FetchStatus.SUCCESS) {
      closeModal();
      resetOrganizationRemoveDeviceStatus();
    }
  }, [status]);

  const footer: JSX.Element = useMemo(() => {
    return (
      <div className="flex">
        <Button
          text={t(`modal.removeDevice.button.${device.canUnpair ? 'unpair' : 'remove'}`)}
          loading={status === FetchStatus.LOADING}
          type="destructive"
          onClick={remove}
        />
        <Button text={t('modal.removeDevice.button.cancel')} type="tertiary" onClick={closeModal} className="ml-2" />
      </div>
    );
  }, [closeModal, remove, status]);

  return (
    <Modal
      name={ModalName.REMOVE_DEVICE}
      header={t(`modal.removeDevice.title.${device.canUnpair ? 'unpair' : 'remove'}`)}
      footer={footer}
    >
      <span className="typography-paragraph-3 text-text-secondary whitespace-pre-line">
        {t(`modal.removeDevice.description.${device.canUnpair ? 'unpair' : 'remove'}`)}
      </span>
    </Modal>
  );
};
